import { WalletTypes } from '@/constants/general';
import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { ErrorMsg, FileWithPath } from '@/types/common';
import useAbortController from '@/utils/hooks/useAbortController';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import { getErrorMessage } from '@/utils/providers/form';
import { useSso } from '@/utils/providers/SSOProvider';
import { Tab } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import cn from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { Button } from '../common/Button';
import Divider from '../common/Divider';
import FormStepper from '../form/components/FormStepper';
import { getDefaultCurrencyId } from '../form/schema/common';
import { PortalWhitelistRequestFormInput, portalWhitelistRequestSchema } from '../form/schema/portalWhitelistSchema';
import ErrorMessage from '../inputs/ErrorMessage';
import { RequestFileProofUpload } from '../inputs/RequestFileProofUpload';
import RequestBankInputs from '../modules/Portal/Request/RequestBankInputs';
import RequestWalletInputs from '../modules/Portal/Request/RequestWalletInputs';
import { SkipWalletValidationModal } from '../modules/Portal/Request/SkipWalletValidationModal';
import Modal, { ModalHeader, ModalProps, ModalTitle } from './Modal';

interface PortalWhitelistModalProps extends ModalProps {
    mode: 'bank' | 'wallet';
    onRefresh: () => void;
}

type PortalWhitelistRequestFormInputKeys = keyof PortalWhitelistRequestFormInput;

const defaultClientId = { label: 'Select an entity', value: undefined };
const defaultCurrencyId = getDefaultCurrencyId('', 'currency');
const defaultNetworkId = { label: 'Select a network', value: undefined };
const defaultWalletType = { label: 'Select a wallet type', value: undefined };
const defaultVaspType = { label: 'Select a VASP', value: undefined };

const defaultValues: Partial<PortalWhitelistRequestFormInput> = {
    friendlyName: '',
    bankName: '',
    bankAddress: '',
    baseCurrencyId: defaultCurrencyId,
    swiftCode: '',
    ibanCode: '',
    accountNumber: '',
    correspondentBankName: '',
    correspondentBankSwiftCode: '',
    intermediaryBankName: '',
    intermediaryBankSwiftCode: '',
    entityId: defaultClientId,
    requestDescription: '',
    networkId: defaultNetworkId,
    // default to false for creating
    skipWalletAddressValidation: false,
    assetsId: [],
    proofOfOwnership: null,
    type: defaultWalletType,
    vaspId: defaultVaspType,
    walletAddress: ''
};

const pageFields: {
    page: number;
    fields: PortalWhitelistRequestFormInputKeys[];
}[] = [
    {
        page: 0,
        fields: [
            'entityId',
            'friendlyName',
            'baseCurrencyId',
            'bankName',
            'bankAddress',
            'swiftCode',
            'ibanCode',
            'assetsId',
            'type',
            'walletAddress',
            'networkId',
            'walletTermsAndConditions'
        ]
    },
    {
        page: 1,
        fields: []
    },
    {
        page: 2,
        fields: ['proofOfOwnership']
    }
];

const PortalWhitelistModal = (props: PortalWhitelistModalProps) => {
    const { opened, handlers, mode, onRefresh } = props;

    const lang = useLanguage();

    const steps = useMemo(
        () =>
            [
                { key: 1, label: lang.portal.requestDetails },
                { key: 2, label: lang.portal.additionalInformation },
                {
                    key: 3,
                    label: mode === 'wallet' ? lang.portal.proofOfOwnershipOptional : lang.portal.proofOfOwnership
                }
            ].filter((step) => (step.key !== 2 && mode === 'wallet') || mode === 'bank'),
        [lang, mode]
    );

    const [error, setError] = useState<ErrorMsg>({ message: '' });
    const [files, setFiles] = useState<FileWithPath[]>([]);
    const [tabActiveIndex, setTabActiveIndex] = useState(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [walletPayload, setWalletPayload] = useState<any>({});

    const { abortController, setAbortController, onClose } = useAbortController({
        isLoading,
        handlers
    });

    const skipWalletDisclosure = useDisclosure(false);

    const { foundEntity, jwt, blockchainsApi, vaspApi } = useSso();
    const { on401Error } = use401Error();

    const isBankWhitelist = useMemo(() => mode === 'bank', [mode]);

    const form = useForm<PortalWhitelistRequestFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(portalWhitelistRequestSchema(isBankWhitelist))
    });

    const { reset, trigger, setValue, handleSubmit, clearErrors } = form;

    const onSubmit = async (data: PortalWhitelistRequestFormInput) => {
        const dataValidated = data as PortalWhitelistRequestFormInput;
        let payload = {};
        setIsLoading(true);
        try {
            if (foundEntity) {
                let whitelist: any = {};
                if (mode === 'bank') {
                    payload = {
                        bankAddress: dataValidated.bankAddress,
                        entityId: foundEntity?.entityId,
                        baseCurrencyId: dataValidated?.baseCurrencyId?.value,
                        correspondentBankName: dataValidated.correspondentBankName || null,
                        correspondentBankSwiftCode: dataValidated.correspondentBankSwiftCode || null,
                        intermediaryBankName: dataValidated.intermediaryBankName || null,
                        intermediaryBankSwiftCode: dataValidated.intermediaryBankSwiftCode || null,
                        ibanCode: dataValidated.ibanCode || null,
                        accountNumber: dataValidated.accountNumber || null,
                        requestDescription: dataValidated.requestDescription || null,
                        swiftCode: dataValidated.swiftCode,
                        friendlyName: dataValidated.friendlyName,
                        bankName: dataValidated.bankName
                    };
                    if (portalURL) {
                        whitelist = await axios.post(portalAPI('/api/whitelist/bank-info/create'), payload, {
                            headers: { Authorization: `Bearer ${jwt}` },
                            signal: abortController.signal
                        });
                    }
                } else {
                    payload = {
                        networkId: dataValidated?.networkId?.value,
                        assetIds: dataValidated?.assetsId?.map((asset) => asset.value),
                        walletAddress: dataValidated.walletAddress,
                        requestDescription: dataValidated.requestDescription || null,
                        friendlyName: dataValidated.friendlyName,
                        entityId: foundEntity?.entityId,
                        type: dataValidated?.type?.value,
                        vaspId:
                            dataValidated?.type?.value === WalletTypes.Regulated
                                ? dataValidated?.vaspId?.value
                                : undefined,
                        tagMemo: dataValidated.tagMemo || null,
                        skipWalletAddressValidation: 'false'
                    };
                    if (portalURL) {
                        whitelist = await axios.post(portalAPI('/api/whitelist/wallet/create'), payload, {
                            headers: { Authorization: `Bearer ${jwt}` },
                            signal: abortController.signal
                        });
                    }
                }

                const requestIdValue = whitelist?.data?.id;

                if (files && files.length > 0) {
                    await uploadFile(requestIdValue, foundEntity?.entityId, files);
                    setFiles([]);
                }
            }
            onRefresh();
            handlers.close();
        } catch (err: any) {
            const walletValidationErrorRegex = /Invalid.+wallet address|Wallet address validator is not prepared/;

            if (err?.response?.data?.message && walletValidationErrorRegex.test(err?.response?.data?.message)) {
                setWalletPayload(payload);
                skipWalletDisclosure[1].open();
            } else {
                setError(err?.response?.data || err);
            }

            on401Error(error, () => {
                logToServer(
                    'error',
                    `Unauthenticated access on whitelist insert modal. JWT may be invalid or expired.`
                );
            });
        }
        setIsLoading(false);
    };

    const uploadFile = useCallback(
        async (requestId: number, entityId: number, file: FileWithPath[]) => {
            if (file.length === 0) return false;

            const formData = new FormData();
            formData.append('file', file[0]);
            formData.append('entityId', entityId.toString());

            if (portalURL) {
                return await axios.post(portalAPI(`/api/requests/${requestId}/file-proof`), formData, {
                    headers: { Authorization: `Bearer ${jwt}` },
                    signal: abortController.signal
                });
            }
        },
        [files, abortController]
    );

    const onError = useCallback((e: FieldErrors) => {
        const errorFieldKeys = Object.keys(e) as PortalWhitelistRequestFormInputKeys[];

        let index;
        if (errorFieldKeys.length > 0) {
            pageFields.find((page) => {
                if (page.fields.includes(errorFieldKeys[0])) {
                    index = page.page;
                    setTabActiveIndex(page.page);
                }
            });
        }
        if (index === 2) {
            const message = getErrorMessage(e);
            setError({ message });
        }
    }, []);

    useEffect(() => {
        if (isBankWhitelist) {
            if (files.length) setValue('proofOfOwnership', 'File Uploaded');
            else setValue('proofOfOwnership', null);
            trigger('proofOfOwnership');
        }
    }, [files, isBankWhitelist]);

    useEffect(() => {
        if (opened) {
            clearErrors();
            reset(defaultValues);
            setFiles([]);
            setError({ message: '' });
            setTabActiveIndex(0);
            setAbortController(new AbortController());

            if (mode === 'wallet') {
                vaspApi.mutate();
                blockchainsApi.mutate();
            }
        } else abortController.abort();

        return () => abortController.abort();
    }, [opened]);
    return (
        <Fragment>
            <Modal {...props} className="h-auto" size="max-w-xl">
                <ModalHeader>
                    <ModalTitle>
                        {mode === 'bank' ? lang.portal.addBankAccount : lang.portal.addWalletAddress}
                    </ModalTitle>
                    {/* <ModalClose handlers={handlers} /> */}
                </ModalHeader>
                <Divider neutral />
                <FormProvider {...form}>
                    <form onSubmit={handleSubmit(onSubmit, onError)} className="relative h-full w-full text-brand-text">
                        <FormStepper.Main activeStep={tabActiveIndex} onStep={(step) => setTabActiveIndex(step)}>
                            {steps.map(({ label }, index) => (
                                <FormStepper.Step key={label} step={index} label={label} />
                            ))}
                        </FormStepper.Main>
                        <Tab.Group
                            defaultIndex={0}
                            selectedIndex={tabActiveIndex}
                            onChange={(index) => setTabActiveIndex(index)}>
                            <Tab.List className="hidden">
                                {steps.map(({ label }) => (
                                    <Tab key={label} className="text-sm">
                                        {label}
                                    </Tab>
                                ))}
                            </Tab.List>
                            <Tab.Panels className="flex-1 basis-0 sm:flex-auto pb-4 w-full h-[500px] overflow-y-auto px-2 sm:px-4">
                                <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                                    {isBankWhitelist ? (
                                        <RequestBankInputs.Section1 />
                                    ) : (
                                        <RequestWalletInputs.Section1 />
                                    )}
                                    <ErrorMessage error={error} />
                                </Tab.Panel>
                                {isBankWhitelist && (
                                    <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                                        <RequestBankInputs.Section2 />
                                        <ErrorMessage error={error} />
                                    </Tab.Panel>
                                )}
                                <Tab.Panel className={cn('flex flex-col min-h-96')}>
                                    <div className="flex-1 basis-0">
                                        <RequestFileProofUpload files={files} setError={setError} setFiles={setFiles} />
                                        <ErrorMessage error={error} className="mt-4" />
                                    </div>
                                </Tab.Panel>
                            </Tab.Panels>
                        </Tab.Group>
                        <Divider neutral />
                        <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                            <button
                                type="button"
                                className="rounded-md p-2 px-4 bg-brand-neutral-600 hover:bg-brand-neutral-500"
                                onClick={() => {
                                    if (tabActiveIndex === 0) onClose();
                                    else setTabActiveIndex((prev) => prev - 1);
                                }}>
                                {tabActiveIndex === 0 ? lang.common.cancel : lang.portal.previousStep}
                            </button>
                            <Button
                                type="button"
                                className="w-auto rounded-md p-2 px-4"
                                onClick={() => {
                                    if (steps.length - 1 === tabActiveIndex) handleSubmit(onSubmit, onError)();
                                    else setTabActiveIndex((prev) => prev + 1);
                                }}
                                isLoading={isLoading}>
                                {steps.length - 1 === tabActiveIndex ? lang.portal.submitRequest : lang.portal.nextStep}
                            </Button>
                        </div>
                    </form>
                </FormProvider>
            </Modal>
            <SkipWalletValidationModal
                payload={walletPayload}
                onClose={() => {
                    onRefresh();
                    handlers.close();
                }}
                setError={setError}
                opened={skipWalletDisclosure[0]}
                handlers={skipWalletDisclosure[1]}
                mode={'Create'}
                files={files}
            />
        </Fragment>
    );
};

export default PortalWhitelistModal;
